/**
 * @fileoverview A program's substitute requests (docs/substitute-requests.md):
 * what instructors have asked for, and what became of it.
 *
 * "To review" first, because it is the only list here anybody is waiting on —
 * an instructor who asked on Monday for next Tuesday needs an answer, and the
 * dashboard's Needs attention line sends the director here. Approving names
 * who covers (a substitute, one of the org's instructors, or "confirm later");
 * declining says why. Either way the instructor is emailed.
 *
 * Approved requests keep showing who covers and whether their link is live,
 * with a way to name the cover later or send a fresh link, so the whole life
 * of a request is readable in one place.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button, Card, ListRow, SectionHeader, Tag, TextTabs } from "@/components/studio/primitives";
import AssignSubstituteDialog from "@/components/org/AssignSubstituteDialog";
import { dayLabel } from "@/lib/orgReporting";
import { coverName } from "@/lib/substitutes";

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

function clock(time) {
  if (!time) return "";
  const [h, m] = String(time).split(":").map(Number);
  if (Number.isNaN(h)) return time;
  return new Date(2000, 0, 1, h, m || 0).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

const when = (r) => [dayLabel(r.date), clock(r.time_local)].filter(Boolean).join(" · ");

/** Approve one request: who covers, and a note for the instructor. */
function ApproveDialog({ request, substitutes, onClose, onDone }) {
  const [kind, setKind] = useState("roster");
  const [substituteId, setSubstituteId] = useState("");
  const [instructorId, setInstructorId] = useState("");
  const [instructors, setInstructors] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    orgApi
      .get("/org/instructors")
      .then((res) => setInstructors((res.data?.instructors || []).filter((i) => i.user_id !== request.requested_by)))
      .catch(() => setInstructors([]));
  }, [request.requested_by]);

  const approve = async () => {
    setSaving(true);
    try {
      await orgApi.post(`/org/substitute-requests/${request.request_id}/approve`, {
        substitute_id: kind === "roster" ? substituteId || null : null,
        instructor_user_id: kind === "instructor" ? instructorId || null : null,
        note: note.trim() || null,
      });
      toast.success(`Approved — ${request.requester_name || "the instructor"} has been emailed`);
      onDone();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't approve that request"));
    } finally {
      setSaving(false);
    }
  };

  const named = (kind === "roster" && substituteId) || (kind === "instructor" && instructorId);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-semibold">Approve substitute request</DialogTitle>
        </DialogHeader>
        <p className="font-sans text-[14px] text-[var(--text-secondary)]">
          {request.requester_name} · {request.class_name} · {when(request)}
        </p>
        {request.reason && (
          <p className="mt-2 font-sans text-[14px] text-[var(--text-primary)]" data-testid="approve-reason">
            &ldquo;{request.reason}&rdquo;
          </p>
        )}
        {request.lesson_plan && (
          <div className="mt-3 rounded-[12px] border border-[var(--studio-divider)] bg-[var(--studio-overlay)] p-3" data-testid="approve-plan">
            <div className="font-sans text-[12px] font-semibold text-[var(--text-tertiary)]">Lesson plan</div>
            <p className="mt-1 whitespace-pre-wrap font-sans text-[14px] text-[var(--text-primary)]">{request.lesson_plan}</p>
          </div>
        )}

        <TextTabs
          testId="approve-kind"
          className="mt-5"
          value={kind}
          onChange={setKind}
          options={[
            { id: "roster", label: "Substitute", testId: "approve-kind-roster" },
            { id: "instructor", label: "One of your instructors", testId: "approve-kind-instructor" },
            { id: "later", label: "Confirm later", testId: "approve-kind-later" },
          ]}
        />
        {kind === "roster" && (
          <select
            value={substituteId}
            onChange={(e) => setSubstituteId(e.target.value)}
            aria-label="Substitute"
            data-testid="approve-substitute"
            className={`${inputClass} mt-4`}
          >
            <option value="">{substitutes.length ? "Choose a substitute" : "No substitutes on file yet"}</option>
            {substitutes.map((x) => (
              <option key={x.substitute_id} value={x.substitute_id}>
                {x.name}
              </option>
            ))}
          </select>
        )}
        {kind === "instructor" && (
          <select
            value={instructorId}
            onChange={(e) => setInstructorId(e.target.value)}
            aria-label="Instructor"
            data-testid="approve-instructor"
            className={`${inputClass} mt-4`}
          >
            <option value="">{instructors ? "Choose an instructor" : "Loading…"}</option>
            {(instructors || []).map((i) => (
              <option key={i.user_id} value={i.user_id}>
                {i.name || i.email}
              </option>
            ))}
          </select>
        )}
        {kind === "later" && (
          <p className="mt-4 font-sans text-[13px] text-[var(--text-tertiary)]">
            The instructor is told the session is covered and that you&rsquo;ll confirm who. Name the substitute from
            this list or the class page when you know.
          </p>
        )}
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="A note for the instructor (optional)"
          rows={2}
          aria-label="Note for the instructor"
          data-testid="approve-note"
          className={`${inputClass} mt-4`}
        />
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Button onPress={approve} disabled={saving || (kind !== "later" && !named)} data-testid="approve-submit">
            {saving ? "Approving…" : "Approve"}
          </Button>
          <button type="button" onClick={onClose} className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline">
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Decline one request, with a reason the instructor reads. */
function DeclineDialog({ request, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const decline = async () => {
    setSaving(true);
    try {
      await orgApi.post(`/org/substitute-requests/${request.request_id}/decline`, { note: note.trim() || null });
      toast.success(`Declined — ${request.requester_name || "the instructor"} has been emailed`);
      onDone();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't decline that request"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-semibold">Decline substitute request</DialogTitle>
        </DialogHeader>
        <p className="font-sans text-[14px] text-[var(--text-secondary)]">
          {request.requester_name} · {request.class_name} · {when(request)}
        </p>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Why, for the instructor (optional)"
          rows={3}
          aria-label="Reason for declining"
          data-testid="decline-note"
          className={`${inputClass} mt-4`}
        />
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Button variant="ghost" onPress={decline} disabled={saving} data-testid="decline-submit">
            {saving ? "Declining…" : "Decline request"}
          </Button>
          <button type="button" onClick={onClose} className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline">
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * @param {{ programId: string, substitutes: Array, onChanged?: () => void }} props
 */
export default function SubstituteRequestsCard({ programId, substitutes = [], onChanged }) {
  const [rows, setRows] = useState(null);
  const [view, setView] = useState("pending");
  const [approving, setApproving] = useState(null);
  const [declining, setDeclining] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [resending, setResending] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await orgApi.get(`/org/substitute-requests?program_id=${encodeURIComponent(programId)}`);
      setRows(res.data?.requests || []);
    } catch {
      setRows({ failed: true });
    }
  }, [programId]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    return {
      pending: list.filter((r) => r.status === "pending").sort((a, b) => `${a.date}${a.time_local}`.localeCompare(`${b.date}${b.time_local}`)),
      approved: list.filter((r) => r.status === "approved"),
      closed: list.filter((r) => r.status === "declined" || r.status === "withdrawn"),
    };
  }, [rows]);
  const shown = groups[view] || [];

  const done = () => {
    setApproving(null);
    setDeclining(null);
    load();
    onChanged?.();
  };

  const resend = async (r) => {
    setResending(r.request_id);
    try {
      // The request's own meeting, not the first one on its date.
      await orgApi.post(`/org/classes/${r.class_id}/sessions/substitute/resend`, {
        date: r.date,
        ...(r.occurrence_id ? { occurrence_id: r.occurrence_id } : {}),
      });
      toast.success(`Sent ${r.assignment?.name || "the substitute"} a fresh link`);
      load();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't send the link"));
    } finally {
      setResending(null);
    }
  };

  const trailing = (r) => {
    if (r.status === "pending") {
      return (
        <span className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onPress={() => setDeclining(r)} data-testid={`sub-request-decline-${r.request_id}`}>
            Decline
          </Button>
          <Button size="sm" onPress={() => setApproving(r)} data-testid={`sub-request-approve-${r.request_id}`}>
            Approve
          </Button>
        </span>
      );
    }
    if (r.status === "approved") {
      const a = r.assignment;
      if (!a || a.kind === "unnamed") {
        return (
          <Button variant="ghost" size="sm" onPress={() => setAssigning(r)} data-testid={`sub-request-name-${r.request_id}`}>
            Name the substitute
          </Button>
        );
      }
      return (
        <span className="flex items-center gap-2">
          {a.lesson_id ? <Tag>Notes written</Tag> : a.kind === "roster" && !a.link_active ? <Tag>Link inactive</Tag> : null}
          {a.kind === "roster" && a.email && !a.lesson_id && (
            <Button
              variant="ghost"
              size="sm"
              onPress={() => resend(r)}
              disabled={resending === r.request_id}
              data-testid={`sub-request-resend-${r.request_id}`}
            >
              {resending === r.request_id ? "Sending…" : "Resend link"}
            </Button>
          )}
        </span>
      );
    }
    return <Tag>{r.status === "declined" ? "Declined" : "Withdrawn"}</Tag>;
  };

  const subtitle = (r) =>
    [
      r.requester_name,
      when(r),
      r.status === "approved" ? `Covered by ${coverName(r.assignment) || "a substitute (to be confirmed)"}` : null,
      r.status === "pending" && r.reason ? `“${r.reason}”` : null,
      r.status === "pending" && r.lesson_plan ? "Plan attached" : null,
      r.status === "declined" && r.decision_note ? `“${r.decision_note}”` : null,
    ]
      .filter(Boolean)
      .join(" · ");

  return (
    <>
      <SectionHeader>
        <span className="inline-flex items-center gap-2">
          Substitute requests
          {groups.pending.length > 0 && <Tag accent>{groups.pending.length} to review</Tag>}
        </span>
      </SectionHeader>
      <TextTabs
        testId="sub-requests-view"
        className="mb-3"
        value={view}
        onChange={setView}
        options={[
          { id: "pending", label: "To review", count: groups.pending.length },
          { id: "approved", label: "Approved", count: groups.approved.length },
          { id: "closed", label: "Declined", count: groups.closed.length },
        ]}
      />
      <Card padding={0} className="mb-6" data-testid="sub-requests">
        {rows === null ? (
          <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</div>
        ) : rows.failed ? (
          <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">Couldn&rsquo;t load the requests.</div>
        ) : shown.length === 0 ? (
          <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]" data-testid="sub-requests-empty">
            {view === "pending"
              ? "Nothing to review. Instructors request cover from an upcoming session when this program allows it."
              : view === "approved"
                ? "No approved requests yet."
                : "Nothing declined or withdrawn."}
          </div>
        ) : (
          shown.map((r, i) => (
            <ListRow
              key={r.request_id}
              borderBottom={i < shown.length - 1}
              data-testid={`sub-request-${r.request_id}`}
              title={r.class_name}
              subtitle={subtitle(r)}
              wrapText
              trailing={trailing(r)}
            />
          ))
        )}
      </Card>

      {approving && (
        <ApproveDialog request={approving} substitutes={substitutes} onClose={() => setApproving(null)} onDone={done} />
      )}
      {declining && <DeclineDialog request={declining} onClose={() => setDeclining(null)} onDone={done} />}
      <AssignSubstituteDialog
        open={Boolean(assigning)}
        onOpenChange={(v) => !v && setAssigning(null)}
        classId={assigning?.class_id}
        session={
          assigning
            ? { date: assigning.date, time_local: assigning.time_local, occurrence_id: assigning.occurrence_id, substitute: assigning.assignment }
            : null
        }
        substitutes={substitutes}
        excludeUserIds={assigning ? [assigning.requested_by] : []}
        onAssigned={done}
      />
    </>
  );
}
